import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme as antTheme, Spin } from 'antd';
import itIT from 'antd/locale/it_IT';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import './i18n';

// Layouts - caricati eagerly perche sempre necessari
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Protected Route Components
import ProtectedRoute from './components/ProtectedRoute';
import PlatformAdminRoute from './components/PlatformAdminRoute';

// AI Chat Component
import AIChat from './components/AIChat/AIChat';

// Public pages - import diretto (no lazy) per evitare chunk loading issues
import PublicQuoteView from './pages/PublicQuote/PublicQuoteView';

import './App.css';

// Loading fallback per lazy loading
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f5f5f5' }}>
    <Spin size="large" />
  </div>
);

// LAZY LOADED PAGES - Riduce bundle iniziale di ~500KB
const Login = lazy(() => import('./pages/Auth/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClientList = lazy(() => import('./pages/Clients/ClientList'));
const ProductList = lazy(() => import('./pages/Products/ProductList'));
const QuoteList = lazy(() => import('./pages/Quotes/QuoteList'));
const QuoteCreate = lazy(() => import('./pages/Quotes/QuoteCreate'));
const QuoteEdit = lazy(() => import('./pages/Quotes/QuoteEdit'));
const Settings = lazy(() => import('./pages/Settings'));
const SupplierList = lazy(() => import('./pages/Suppliers/SupplierList'));
const TestCatalog = lazy(() => import('./pages/TestCatalog'));
const OptionGroupList = lazy(() => import('./pages/OptionGroups/OptionGroupList'));
const UserList = lazy(() => import('./pages/Users/UserList'));
const UserHierarchy = lazy(() => import('./pages/Users/UserHierarchy'));
const AppointmentList = lazy(() => import('./pages/Appointments/AppointmentList'));
const OpportunityList = lazy(() => import('./pages/Opportunities/OpportunityList'));
const NotificationList = lazy(() => import('./pages/Notifications/NotificationList'));
const TenantSelectionPage = lazy(() => import('./pages/TenantSelectionPage'));
const TenantsPage = lazy(() => import('./pages/admin/TenantsPage'));
const TenantDetailPage = lazy(() => import('./pages/admin/TenantDetailPage'));
const TenantUsersPage = lazy(() => import('./pages/admin/TenantUsersPage'));
const TeamCommercialeList = lazy(() => import('./pages/TeamCommerciale/TeamCommercialeList'));
const AuditLogPage = lazy(() => import('./pages/AuditLogs/AuditLogPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const EmailTestPage = lazy(() => import('./pages/admin/EmailTestPage'));
const AIAdminPage = lazy(() => import('./pages/admin/AIAdminPage'));
const OrderConfirmationList = lazy(() => import('./pages/OrderConfirmations/OrderConfirmationList'));
const OrderConfirmationDetail = lazy(() => import('./pages/OrderConfirmations/OrderConfirmationDetail'));
const UserProfile = lazy(() => import('./pages/UserProfile/UserProfile'));

const App: React.FC = () => {
  return (
    <ConfigProvider
      locale={itIT}
      theme={{
        algorithm: antTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#2563eb',
          borderRadius: 8,
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
      }}
    >
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Auth Routes */}
                <Route path="/login" element={<AuthLayout><Login /></AuthLayout>} />

                {/* Tenant Selection (after login for multi-tenant users) */}
                <Route path="/select-tenant" element={<TenantSelectionPage />} />

                {/* Public Routes (no auth required) */}
                <Route path="/public/quote/:token" element={<PublicQuoteView />} />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="clients" element={<ClientList />} />
                  <Route path="appointments" element={<AppointmentList />} />
                  <Route path="opportunities" element={<OpportunityList />} />
                  <Route path="products" element={<ProductList />} />
                  <Route path="suppliers" element={<SupplierList />} />
                  <Route path="option-groups" element={<OptionGroupList />} />
                  <Route path="quotes" element={<QuoteList />} />
                  <Route path="quotes/new" element={<QuoteCreate />} />
                  <Route path="quotes/:id/edit" element={<QuoteEdit />} />
                  {/* Redirect old quote links without /edit */}
                  <Route path="quotes/:id" element={<Navigate to="edit" replace />} />
                  <Route path="order-confirmations" element={<OrderConfirmationList />} />
                  <Route path="order-confirmations/:id" element={<OrderConfirmationDetail />} />
                  <Route path="users" element={<Navigate to="/admin/users" replace />} />
                  <Route path="users/hierarchy" element={<UserHierarchy />} />
                  <Route path="team-commerciale" element={<TeamCommercialeList />} />
                  <Route path="notifications" element={<NotificationList />} />
                  <Route path="profile" element={<UserProfile />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="/test-catalog" element={<TestCatalog />} />

                  {/* Audit Logs - Tenant Admin */}
                  <Route path="audit-logs" element={<AuditLogPage />} />

                  {/* Platform Admin Routes */}
                  <Route path="admin/dashboard" element={<PlatformAdminRoute><AdminDashboard /></PlatformAdminRoute>} />
                  <Route path="admin/tenants" element={<PlatformAdminRoute><TenantsPage /></PlatformAdminRoute>} />
                  <Route path="admin/tenants/new" element={<PlatformAdminRoute><TenantDetailPage /></PlatformAdminRoute>} />
                  <Route path="admin/tenants/:tenantId" element={<PlatformAdminRoute><TenantDetailPage /></PlatformAdminRoute>} />
                  <Route path="admin/tenants/:tenantId/users" element={<PlatformAdminRoute><TenantUsersPage /></PlatformAdminRoute>} />
                  <Route path="admin/users" element={<PlatformAdminRoute><AdminUsersPage /></PlatformAdminRoute>} />
                  <Route path="admin/audit-logs" element={<PlatformAdminRoute><AuditLogPage /></PlatformAdminRoute>} />
                  <Route path="admin/email-test" element={<PlatformAdminRoute><EmailTestPage /></PlatformAdminRoute>} />
                  <Route path="admin/ai" element={<PlatformAdminRoute><AIAdminPage /></PlatformAdminRoute>} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>

          {/* AI Chat FAB (visible on all pages) */}
          <AIChat apiBaseUrl={process.env.REACT_APP_API_URL + '/ai'} />
        </PermissionsProvider>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
